import { useTranslation } from '@refinedev/core';
// Mantine
import { Button, Tooltip, useComputedColorScheme } from '@mantine/core';
import { IconRestore } from '@tabler/icons-react';

// MRT
import type { MRT_RowData, MRT_TableInstance } from 'mantine-react-table';

import {
  getDefaultColumnVisibility,
  getDefaultVisibleColumns,
  getProviderColumnOrder,
  CLAIM_ACTION_COLUMN_ID,
} from './ClaimConstants';
import { useCurrentSiteStore } from 'store/SiteStore';
interface RestoreColumnsButtonProps<TData extends MRT_RowData> {
  table: MRT_TableInstance<TData>;
  isProvider: boolean;
}

export const RestoreColumnsButton = <TData extends MRT_RowData>({
  table,
  isProvider,
}: RestoreColumnsButtonProps<TData>) => {
  const { translate } = useTranslation();
  const toolbarIconSize = 24;
  const toolbarButtonSize = 32;
  const computedColorScheme = useComputedColorScheme('dark');
  const { providerSiteSelection } = useCurrentSiteStore();
  const isAllSites = providerSiteSelection === 'all';

  const handleRestore = () => {
    const defaultVisibleKeys = getDefaultVisibleColumns(isProvider);

    const leafColumns = table.getAllLeafColumns();
    const columnIDs = leafColumns.map((c) => c.id);
    const orderedColumnIDs = isProvider
      ? getProviderColumnOrder(columnIDs)
      : columnIDs;

    const visibility = getDefaultColumnVisibility(
      leafColumns.map((c) => ({
        accessorKey: c.id,
        header: c.columnDef.header,
      })),
      defaultVisibleKeys,
      null,
      isProvider,
      isAllSites
    );

    visibility[CLAIM_ACTION_COLUMN_ID] = true;

    table.setColumnOrder([
      CLAIM_ACTION_COLUMN_ID,
      ...orderedColumnIDs.filter((ID) => ID !== CLAIM_ACTION_COLUMN_ID),
    ]);
    table.setColumnVisibility(visibility);
  };

  return (
    <Tooltip
      label={translate(
        'pages:claims.table.restoreColumns',
        'Restore Default Columns'
      )}
    >
      <Button
        onClick={handleRestore}
        variant="subtle"
        size="xs"
        p={0}
        style={{
          width: toolbarButtonSize,
          height: toolbarButtonSize,
          minWidth: toolbarButtonSize,
        }}
      >
        <IconRestore
          size={toolbarIconSize}
          color={computedColorScheme === 'dark' ? 'white' : 'black'}
        />
      </Button>
    </Tooltip>
  );
};
